import React, { useContext, useEffect } from 'react';
import { ITypeAgbListFields, IValidatedState, IVignetteContext, VignetteContext } from '@adac-mp/shared';
import { Checkbox, Richtext, Typography, renderErrorMessage } from '../../index';
import { useValidatedStates } from '../../hooks/useValidatedStates';

export interface IAGBItem {
	id: string;
	text: string | React.ReactNode;
}

interface ITermsAndConditionsProps {
	validateFields?: boolean;
	additionalText?: string | React.ReactNode;
	agbs?: IAGBItem[] | ITypeAgbListFields;
	updateButtonState: (isChecked: boolean) => void;
}

export const TermsAndConditions = ({
	agbs,
	validateFields,
	additionalText,
	updateButtonState,
}: ITermsAndConditionsProps) => {
	const { cartItems } = useContext<IVignetteContext>(VignetteContext);

	const initAgbs = () => {
		if (agbs && Array.isArray(agbs)) {
			return agbs;
		}
		if (agbs && agbs?.agbs) {
			const agbItems = agbs.agbs.items;
			const generalAgbs = agbItems
				.filter(({ category }: any) => category === 'Allgemein' || !category || cartItems.length === 0)
				.map((agb: any, index) => ({
					id: `agb${index}`,
					text: <Richtext document={agb.content} linkTrackingLabel={agb.trackingLabel} />,
				}));
			const hasSloviananProduct = cartItems.find(({ offer }) => offer.supplierId === 7);
			const hasAustriantProduct = cartItems.find(({ offer }) => offer.supplierId === 3);
			const hasSchweizProduct = cartItems.find(({ offer }) => offer.supplierId === 14);
			const schweizAgb: any = agbItems.find(({category}: any) => category === 'Schweiz')

			if (hasSchweizProduct && schweizAgb) {
				generalAgbs.unshift({
					id: `country-agb-${schweizAgb.category}`,
					text: <Richtext document={schweizAgb.content} linkTrackingLabel={schweizAgb.trackingLabel} />,
				})
			}

			const countryAgbs = agbItems.filter(
				({ category }: any) =>
					((hasSloviananProduct && category === 'Slowenien') ||
						(hasAustriantProduct && category === 'Österreich')) &&
					category !== 'Allgemein' &&
					category,
			);

			if (countryAgbs.length > 0) {
				const mergedAgbs = countryAgbs.reduce(
					(acc, val: any) => ({
						id: `${acc.id}`,
						text: (
							<>
								{acc.text} <Richtext document={val.content} linkTrackingLabel={val.trackingLabel} />
							</>
						),
					}),
					{ id: 'country-agb', text: <></> },
				);

				const countryAgbIndex = agbItems.findIndex(({ category }: any) => category !== 'Allgemein' && category); // Index where to add in the merged Agb
				generalAgbs.splice(countryAgbIndex, 0, mergedAgbs);
			}

			return generalAgbs;
		}
		return [];
	};
	const currentAgbs = initAgbs();
	const fields = currentAgbs.map(({ id }) => id);
	const stateInit = {};
	fields.forEach((field) => {
		stateInit[field] = {
			value: false,
			error: false,
			required: true,
		};
	});

	const [state, { updateWithError, hasErrors, updateAll }] = useValidatedStates(stateInit);

	useEffect(() => {
		if (Object.values(state).length === 0) {
			updateAll(stateInit);
		}
	}, [agbs]);

	const hasError = currentAgbs.length > 0 ? currentAgbs.some(({ id }) => state[id]?.error) : false;

	useEffect(() => {
		const states: IValidatedState[] = Object.values(state);
		const allFilled = states.every(({ value, required }) => (required ? value && !hasErrors() : true));
		updateButtonState(allFilled);
	}, [state]);

	useEffect(() => {
		if (validateFields) {
			currentAgbs.forEach(({ id }) => {
				updateWithError(id, state[id].value, !state[id].value);
			});
		}
	}, [validateFields]);

	return (
		<div className="px-4 mt-5">
			{additionalText && (
				<Typography component="p" className="pb-5">
					{additionalText}
				</Typography>
			)}
			{currentAgbs &&
				Object.values(state).length > 0 &&
				currentAgbs.map(({ id, text }) => (
					<Checkbox
						key={`checkbox-${id}`}
						mandatory
						className="pb-3 last:pb-0"
						data-cy={`Checkbox: ${id}`}
						title={id}
						hasError={state[id]?.error}
						label={text}
						size="sm"
						checked={state[id]?.value}
						setChecked={() => {
							updateWithError(id, !state[id].value, validateFields ? state[id].value : false);
						}}
					/>
				))}
			{hasError &&
				renderErrorMessage({
					message:
						agbs && 'errorText' in agbs
							? agbs.errorText
							: 'Bitte akzeptieren Sie die Allgemeinen Geschäftsbedingungen.',
				})}
		</div>
	);
};
